import { useCallback, useEffect, useRef, useState } from 'react';
import { environment } from '../../environments/environment';
import { extractReasoningEntries } from './flowRunMapping';

export interface ReasoningLine {
    reasoning: string;
    activityTemplateName?: string;
}

/** How long a send waits for the opening snapshot before going ahead without it. */
export const SNAPSHOT_WAIT_MS = 2000;

/**
 * Follows /ws/reasoning-logs for one request while it is in flight, so a chat can show
 * what the agent is doing until /agent/flow/execute answers.
 *
 * The backend does not clear an interaction's log between execute calls: a chat that
 * reuses one interactionId gets every earlier answer's reasoning back in each snapshot
 * and update, which carry the whole array. So `start` resolves only once the socket's
 * opening snapshot has arrived, and the caller sends its request after that. Everything
 * in that snapshot predates the request, its length is the offset, and only entries
 * past it are shown. Entry timestamps cannot do this job: they are written in IST with
 * no zone, or labelled UTC when they are not.
 *
 * If no snapshot arrives in SNAPSHOT_WAIT_MS, the request goes anyway and the longest
 * log seen so far in the session stands in for the offset.
 *
 * `start` hands back a token and `stop` only acts on the latest one: a request that
 * settles after a newer one has started must not close the newer request's socket.
 */
export const useReasoningStream = () => {
    const [entries, setEntries] = useState<ReasoningLine[]>([]);
    // The stream died or reported an error before the answer came back.
    const [interrupted, setInterrupted] = useState(false);
    const socketRef = useRef<WebSocket | null>(null);
    const tokenRef = useRef(0);
    // Longest log seen for this hook's lifetime; the fallback offset.
    const knownLengthRef = useRef(0);
    // Releases a start still waiting on its snapshot when its socket is replaced.
    const releaseRef = useRef<(() => void) | null>(null);

    const close = () => {
        releaseRef.current?.();
        releaseRef.current = null;
        const socket = socketRef.current;
        socketRef.current = null;
        if (!socket) return;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
    };

    const start = useCallback((interactionId: string): Promise<number> => {
        close();
        setEntries([]);
        setInterrupted(false);
        const token = ++tokenRef.current;

        return new Promise<number>((resolve) => {
            let offset: number | null = null;
            const settle = (value: number) => {
                if (offset !== null) return;
                offset = value;
                clearTimeout(timer);
                if (releaseRef.current === release) releaseRef.current = null;
                resolve(token);
            };
            const release = () => settle(knownLengthRef.current);
            const timer = setTimeout(release, SNAPSHOT_WAIT_MS);
            releaseRef.current = release;

            const base = environment.reasoningWsUrl || (environment.controlplaneUrl || '').replace(/^http/, 'ws');
            let socket: WebSocket;
            try {
                socket = new WebSocket(`${base}/ws/reasoning-logs?interactionId=${encodeURIComponent(interactionId)}`);
            } catch (error) {
                console.error('Failed to open reasoning websocket:', error);
                setInterrupted(true);
                settle(knownLengthRef.current);
                return;
            }
            socketRef.current = socket;

            socket.onmessage = (event) => {
                let data;
                try {
                    data = JSON.parse(event.data);
                } catch {
                    // A bare string is a line of reasoning in its own right.
                    data = String(event.data);
                }
                // A server-side failure, not a line of the agent's reasoning.
                if (data?.type === 'error') {
                    setInterrupted(true);
                    settle(knownLengthRef.current);
                    return;
                }
                const { entries: incoming, mode } = extractReasoningEntries(data);
                const all = incoming as (ReasoningLine | null)[];

                if (mode === 'replace') {
                    knownLengthRef.current = Math.max(knownLengthRef.current, all.length);
                } else {
                    knownLengthRef.current += all.length;
                }
                if (data?.type === 'snapshot' && offset === null) {
                    settle(all.length);
                    return;
                }
                // Anything that beats the snapshot here is already history.
                if (offset === null) return;

                const visible = (lines: (ReasoningLine | null)[]) =>
                    lines.filter((e): e is ReasoningLine => Boolean(e?.reasoning?.trim()));
                if (mode === 'replace') setEntries(visible(all.slice(offset)));
                else {
                    const lines = visible(all);
                    if (lines.length) setEntries(prev => [...prev, ...lines]);
                }
            };
            // The answer still arrives over HTTP; a dead stream only costs the preview.
            socket.onerror = (error) => {
                console.error('Reasoning websocket error:', error);
                setInterrupted(true);
                settle(knownLengthRef.current);
            };
            // close() detaches this first, so it only fires when the server hangs up.
            socket.onclose = () => {
                setInterrupted(true);
                settle(knownLengthRef.current);
            };
        });
    }, []);

    const stop = useCallback((token: number) => {
        if (token !== tokenRef.current) return;
        close();
        setEntries([]);
        setInterrupted(false);
    }, []);

    useEffect(() => close, []);

    return { entries, interrupted, start, stop };
};
